import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import {
  Collapse,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  ContentState,
  EditorState,
  convertFromHTML,
  convertToRaw,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';

import { getConfigState } from '../../../../store/ConfigSlice';
import {
  getCoursesState,
  resetAdminCourse,
  TCreateCourseAsAdmin,
  createCourseAsAdmin,
} from '../../../../store/V2/CoursesSlice';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../../components/BreadCrumb/Breadcrumbs';
import EditableTextWithTextField from '../../../../components/Inputs/EditableTextWithTextField';
import EditableTextWithDraftEditor from '../../../../components/Inputs/EditableTextWithDraftEditor';

type TCourseState = {
  title: string;
  description: string;
  isOneToOne?: boolean;
};

const coursesTabUrl = `${process.env.PUBLIC_URL}/portal/courses`;

const constructBreadcrumbOptions = () => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'link',
      title: 'Courses',
      link: coursesTabUrl,
    },
    {
      type: 'text',
      title: 'New Course',
      highlight: true,
    },
  ];

  options.push();

  return options;
};

const getDefaultDescription = () => {
  const _blocksFromHTML = convertFromHTML('');
  const _editorState = ContentState.createFromBlockArray(
    _blocksFromHTML.contentBlocks,
    _blocksFromHTML.entityMap
  );

  const _editText = EditorState.createWithContent(_editorState);

  return draftToHtml(convertToRaw(_editText.getCurrentContent()));
};

const initialCourseState: TCourseState = {
  title: 'Course Title',
  description: getDefaultDescription(),
  isOneToOne: false,
};

const CreateNewCoursePage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const configState = useAppSelector(getConfigState);
  const coursesState = useAppSelector(getCoursesState);

  const [courseState, setCourseState] = useState(initialCourseState);
  const [isClassDetailsCollapseOpen, setClassDetailsCollapseOpen] = useState(
    true
  );

  const urlSearchParams = new URLSearchParams(location.search);
  const urlIsOneToOneValue = urlSearchParams.get('type') === '1-1';
  const breadcrumbOptions = constructBreadcrumbOptions();
  const isSubmitDisabled =
    !courseState.title.trim().length ||
    !courseState.description.trim().length ||
    coursesState.status === 'loading';

  // useEffect(() => {
  //   if (urlIsOneToOneValue) {
  //     setCourseState((prevState) => ({
  //       ...prevState,
  //       isOneToOne: urlIsOneToOneValue,
  //     }));
  //   }
  // }, []);

  useEffect(() => {
    if (coursesState.adminCourse) {
      const redirectUrl = `${process.env.PUBLIC_URL}/portal/courses/${coursesState.adminCourse._id}`;
      dispatch(resetAdminCourse());

      navigate(redirectUrl, { replace: true });
    }
  }, [coursesState.adminCourse]);

  const toggleClassDetailsCollapse = () => {
    setClassDetailsCollapseOpen(!isClassDetailsCollapseOpen);
  };

  const updateCourseState = (attributes: Partial<TCourseState>) => {
    setCourseState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = () => {
    const apiData: TCreateCourseAsAdmin = {
      title: courseState.title,
      description: courseState.description,
    };

    if (courseState.isOneToOne) {
      apiData.isOneToOne = courseState.isOneToOne;
    }

    dispatch(createCourseAsAdmin(apiData));
  };

  if (!configState.adminEditMode) {
    return <Navigate replace to={coursesTabUrl} />;
  }

  return (
    <>
      <Container maxWidth="lg">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor="#647488"
            showDropDown={isXsScreen}
          />
        </div>
        <Stack direction="row" justifyContent="flex-end" mb="15px" gap={2}>
          {/* <FormControlLabel
            label="1-1 Course"
            control={
              <Switch
                checked={courseState.isOneToOne}
                onChange={(event) =>
                  updateCourseState({
                    isOneToOne: event.target.checked,
                  })
                }
              />
            }
          /> */}
          <LoadingButton
            loading={coursesState.status === 'loading'}
            disabled={isSubmitDisabled}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleSubmit}
          >
            Create Course
          </LoadingButton>
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" flexGrow={1}>
              <IconButton
                style={{ marginRight: '5px' }}
                onClick={toggleClassDetailsCollapse}
              >
                {isClassDetailsCollapseOpen ? (
                  <ExpandLessIcon style={{ color: TP_COLORS.black }} />
                ) : (
                  <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
                )}
              </IconButton>
              <EditableTextWithTextField
                label={courseState.title}
                typographyProps={{
                  variant: 'h5',
                  fontSize: '25px',
                  fontWeight: 700,
                }}
                onSubmit={(newTitle) =>
                  updateCourseState({
                    title: newTitle,
                  })
                }
              />
            </Stack>
            <Collapse in={isClassDetailsCollapseOpen}>
              <div style={{ padding: `15px ${isXsScreen ? '15px' : '50px'}` }}>
                <div style={{ marginBottom: '30px' }}>
                  <EditableTextWithDraftEditor
                    label={courseState.description}
                    onSubmit={(newDescription) => {
                      updateCourseState({
                        description: newDescription,
                      });
                    }}
                  />
                </div>
              </div>
            </Collapse>
            <div
              style={{
                height: '1px',
                borderBottom: '1px dotted #94A3B8',
                marginTop: '30px',
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default CreateNewCoursePage;
